import { Divider } from '@ui-kitten/components';
import dayjs from 'dayjs';
import lodashIsEmpty from 'lodash/isEmpty';
import { Fragment, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';

import CalendarIcon from '../../Assets/calendar.svg';
import ListItem from '../../Components/ListItem';
import { textCategory } from '../../Components/Reservation/constants';
import ResponsiveText from '../../Components/ResponsiveText';
import TicketIcon from '../../Components/SVG/Tickets';
import constants from '../../Config/constants';
import { isValidJson, pluralize } from '../../Helper';
import dateHelper from '../../Helper/Date';
import { getCurrency } from '../../Helper/Routines';
import storeHelper from '../../Helper/Store';
import {
  CustomerNotesJSONstring,
  OrderDetails,
} from '../../RTK/api/orders/types';
import themeColor from '../../Theme/colors';
import themeStyle from '../../Theme/styles';
import DisplaySelectedSamples from '../ReservationCheckout/components/ReservationLeftColumnContent/DisplaySelectedSamples';

const ReservationDetails = ({ data }: { data: OrderDetails }) => {
  const firstItem = data?.items?.[0];
  const secondItem = data?.items?.[1];
  const parsedRemarks: CustomerNotesJSONstring = useMemo(
    () =>
      isValidJson(data?.customer_notes)
        ? JSON.parse(data.customer_notes)
        : undefined,
    [data?.customer_notes],
  );

  const infos = useMemo(() => {
    const isReservation = lodashIsEmpty(data?.event_details);
    const reservationTime = dateHelper.getDayjsAtTime(data?.order_time);
    const selectedTime = !isReservation
      ? dayjs(data?.event_details?.start_date).format(
          constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT,
        )
      : `${[
          reservationTime.format(constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT),
          reservationTime
            .add(data?.dining_length, 'minute')
            .format(constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT),
        ].join(' - ')} ( ${storeHelper.getDiningDurationInHours(
          data?.dining_length,
        )} )`;
    return [
      {
        name: dayjs(data?.order_date).format('MMMM DD, YYYY'),
        icon: { leftIcon: { svg: CalendarIcon, size: 20 } },
      },
      {
        name: selectedTime,
        icon: { leftIcon: { name: 'clock-outline', size: 20 } },
      },
      {
        name: `${firstItem?.quantity} ${pluralize(
          firstItem?.quantity,
          isReservation ? 'seat' : 'ticket',
        )}${firstItem?.name ? ` - ${firstItem?.name}` : ''}`,
        icon: { leftIcon: { svg: TicketIcon, size: 22 } },
      },
      ...(!isReservation
        ? [
            {
              name:
                data?.event_details?.location_address ||
                data?.store_details?.location,
              icon: { leftIcon: { name: 'pin-outline' } },
            },
          ]
        : []),
    ];
  }, [
    data?.dining_length,
    data?.event_details?.location_address,
    data?.event_details?.start_date,
    data?.order_date,
    data?.order_time,
    data?.store_details?.location,
    firstItem?.name,
    firstItem?.quantity,
  ]);

  return (
    <Fragment>
      <View style={[styles.boxWrapper, themeStyle.spacingBottomMedium]}>
        <View style={themeStyle.pageVerticalSpacingMedium}>
          <ResponsiveText
            category={textCategory.HEADING}
            style={themeStyle.bold}
          >
            {data.store_details.name}
          </ResponsiveText>
          <ResponsiveText category={textCategory.BODY}>
            {firstItem?.name}
            {secondItem?.name ? ` - ${secondItem?.name}` : ''} &times;{' '}
            {firstItem?.quantity}
          </ResponsiveText>
        </View>
        <Divider style={[themeStyle.divider, themeStyle.spacingBottomMedium]} />
        {infos.map((l) => (
          <ListItem
            key={l.name}
            contentContainerStyle={themeStyle.pageVerticalSpacingMedium}
            title={
              <ResponsiveText category={textCategory.BODY}>
                {l.name}
              </ResponsiveText>
            }
            {...l.icon}
          />
        ))}
      </View>
      {/* show dietary and celebration of not both empty */}
      {!lodashIsEmpty(parsedRemarks) &&
        (!lodashIsEmpty(parsedRemarks?.dietary) ||
          !lodashIsEmpty(parsedRemarks?.celebration)) && (
          <View style={[styles.boxWrapper, themeStyle.spacingBottomMedium]}>
            {!lodashIsEmpty(parsedRemarks?.dietary) && (
              <View style={themeStyle.flexDirectionRowCenterSpaceBetween}>
                <ResponsiveText>Dietary Preference</ResponsiveText>
                <DisplaySelectedSamples values={parsedRemarks?.dietary} />
              </View>
            )}
            {/* show divider if both has value */}
            {!lodashIsEmpty(parsedRemarks?.dietary) &&
              !lodashIsEmpty(parsedRemarks?.celebration) && (
                <Divider
                  style={[
                    themeStyle.divider,
                    themeStyle.spacingTopMedium,
                    themeStyle.spacingBottomMedium,
                  ]}
                />
              )}
            {!lodashIsEmpty(parsedRemarks?.celebration) && (
              <View style={themeStyle.flexDirectionRowCenterSpaceBetween}>
                <ResponsiveText>Celebration</ResponsiveText>
                <DisplaySelectedSamples values={parsedRemarks?.celebration} />
              </View>
            )}
          </View>
        )}
      <View style={styles.boxWrapper}>
        <ListItem
          title="Total Amount Paid"
          accessoryRight={
            <ResponsiveText
              category={textCategory.BODY}
              style={themeStyle.bold}
            >
              {getCurrency(data.total_amount)}
            </ResponsiveText>
          }
        />
      </View>
    </Fragment>
  );
};

const styles = StyleSheet.create({
  boxWrapper: {
    ...themeStyle.pageBackground,
    ...themeStyle.pageVerticalSpacingMedium,
    ...themeStyle.pageHorizontalSpacing,
    borderRadius: 10,
    borderColor: themeColor.gray,
    borderWidth: 1,
  },
});

export default ReservationDetails;
